// components/InlineEditField.jsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { PencilIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";

/**
 * InlineEditField - A reusable inline edit component.
 *
 * @param {function} renderValue - Render prop for readonly value.
 * @param {function} renderEdit - Render prop for edit UI.
 * @param {function} onSave - Called with new value, should return Promise of boolean.
 * @param {function} onCancel - Called when edit is cancelled.
 * @param {boolean} isLoading - If true, hides all action buttons and shows loading indicator.
 * @param {boolean} canEdit - If false, hides edit button and prevents editing.
 * @param {boolean} closeOnClickOutside - If false, clicking outside does not cancel editing (default: true).
 * @param {function} onEditStart - Called when editing starts.
 * @param {function} onEditEnd - Called when editing ends (either save or cancel).
 * @param {string} actionButtonsPosition - Position of action buttons: "inline", "top", "bottom", "left", "right" (default: "inline").
 */
export default function InlineEditField({
  renderValue,
  renderEdit,
  onSave,
  onCancel,
  isLoading = false,
  canEdit = true,
  closeOnClickOutside = true,
  onEditStart,
  onEditEnd,
  actionButtonsPosition = "inline",
  hideDefaultEditButton = false,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const containerRef = useRef(null);
  const actionButtonsRef = useRef(null);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });

  const handleEdit = () => {
    setIsEditing(true);
    onEditStart?.();
  };

  const handleSave = async value => {
    setSaving(true);
    const success = await onSave(value);
    setSaving(false);
    if (success) {
      setIsEditing(false);
      onEditEnd?.();
    }
  };

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    onEditEnd?.();
    onCancel?.();
  }, [onCancel, onEditEnd]);

  // Calculate position for portal-based action buttons
  useEffect(() => {
    if (!isEditing || actionButtonsPosition === "inline" || !containerRef.current) return;

    const updatePosition = () => {
      if (!containerRef.current) return; // Guard against null ref

      const rect = containerRef.current.getBoundingClientRect();
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;

      let top = 0;
      let left = 0;

      switch (actionButtonsPosition) {
        case "top":
          top = rect.top + scrollY - 40; // 40px above
          left = rect.left + scrollX + rect.width / 2 - 40; // Center horizontally
          break;
        case "bottom":
          top = rect.bottom + scrollY + 8; // 8px below
          left = rect.left + scrollX + rect.width / 2 - 40; // Center horizontally
          break;
        case "left":
          top = rect.top + scrollY + rect.height / 2 - 20; // Center vertically
          left = rect.left + scrollX - 88; // 88px to the left (width of buttons + gap)
          break;
        case "right":
          top = rect.top + scrollY + rect.height / 2 - 20; // Center vertically
          left = rect.right + scrollX + 8; // 8px to the right
          break;
        default:
          break;
      }

      setButtonPosition({ top, left });
    };

    updatePosition();

    // Update on scroll or resize
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

     
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isEditing, actionButtonsPosition]);

  // Handle click outside to cancel editing
  useEffect(() => {
    if (!closeOnClickOutside) return; // Explicit for consistent-return
    const handleClickOutside = event => {
      // Check if click is inside the main container
      if (containerRef.current && containerRef.current.contains(event.target)) {
        return;
      }

      // Check if click is inside the action buttons portal
      if (actionButtonsRef.current && actionButtonsRef.current.contains(event.target)) {
        return;
      }

      // Check if click is inside a date picker portal (used by FvDatePicker)
      const datePickerPortal = document.getElementById("root-portal");
      if (datePickerPortal && datePickerPortal.contains(event.target)) {
        return;
      }

      // Check if click is inside any react-datepicker element (fallback)
      const closestDatePicker = event.target.closest(".react-datepicker-popper");
      if (closestDatePicker) {
        return;
      }

      // If we get here, click was truly outside - cancel editing
      handleCancel();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

     
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [handleCancel, closeOnClickOutside]);

  const loadingSpinner = (
    <div className="inline-flex items-center">
      <svg
        className="size-4 animate-spin text-gray-500"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );

  const renderActionButtons = () => {
    if (isLoading) {
      return loadingSpinner;
    }

    const buttons = (
      <>
        <button
          type="button"
          aria-label="Save"
          className="rounded p-1 hover:bg-green-100 dark:hover:bg-green-900"
          onClick={() => (renderEdit().props.value !== undefined ? handleSave(renderEdit().props.value) : handleSave())}
          disabled={saving}
        >
          {saving ? loadingSpinner : <CheckIcon className="size-4 text-green-600" />}
        </button>
        <button
          type="button"
          aria-label="Cancel"
          className="rounded p-1 hover:bg-red-100 dark:hover:bg-red-900"
          onClick={handleCancel}
          disabled={saving}
        >
          <XMarkIcon className="size-4 text-red-600" />
        </button>
      </>
    );

    if (actionButtonsPosition === "inline") {
      return buttons;
    }

    // Render as portal with absolute positioning
    return createPortal(
      <div
        ref={actionButtonsRef}
        className="fixed z-50 flex items-center gap-2 rounded-lg bg-white p-1 shadow-lg ring-1 ring-black/5 dark:bg-gray-800"
        style={{
          top: `${buttonPosition.top}px`,
          left: `${buttonPosition.left}px`,
        }}
      >
        {buttons}
      </div>,
      document.body,
    );
  };

  return (
    <div ref={containerRef} className="inline-flex items-center gap-2">
      {!isEditing ? (
        <>
          {renderValue({ onEdit: canEdit ? handleEdit : undefined })}
          {isLoading && loadingSpinner}
          {!isLoading && canEdit && !hideDefaultEditButton && (
            <button
              type="button"
              aria-label={canEdit ? "Edit" : "Read-only"}
              className={`rounded p-1 ${canEdit ? "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700" : "cursor-not-allowed"}`}
              onClick={canEdit ? handleEdit : undefined}
              disabled={!canEdit}
            >
              <PencilIcon className={`size-4 ${canEdit ? "text-gray-500" : "text-gray-300 dark:text-gray-600"}`} />
            </button>
          )}
        </>
      ) : (
        <>
          {renderEdit({
            onSave: handleSave,
            onCancel: handleCancel,
            saving: saving || isLoading,
          })}
          {renderActionButtons()}
        </>
      )}
    </div>
  );
}

InlineEditField.propTypes = {
  renderValue: PropTypes.func.isRequired,
  renderEdit: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  isLoading: PropTypes.bool,
  canEdit: PropTypes.bool,
  closeOnClickOutside: PropTypes.bool,
  onEditStart: PropTypes.func,
  onEditEnd: PropTypes.func,
  actionButtonsPosition: PropTypes.oneOf(["inline", "top", "bottom", "left", "right"]),
  hideDefaultEditButton: PropTypes.bool,
};
